// JOBE-0044.10: CollectionValuePopover — multi-select of collections.
// Reads `GET /api/collections?lifecycle=active` (matches
// CollectionsRailBlock). v1 issues its own fetch; a Phase 4 follow-up
// can share a cache with the rail block.

import { useEffect, useMemo, useRef, useState } from 'react';

export function CollectionValuePopover({
  initialValues = [],
  onSubmit,
  onCancel,
  fetcher,
}) {
  const [state, setState] = useState({ status: 'loading', items: [], error: null });
  const [selected, setSelected] = useState(() => new Set(initialValues));
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    const doFetch = fetcher || ((url, opts) => fetch(url, opts));
    setState({ status: 'loading', items: [], error: null });
    doFetch('/api/collections?lifecycle=active', {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setState({ status: 'error', items: [], error: `http_${r.status}` });
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const items = Array.isArray(j?.collections) ? j.collections : [];
        setState({ status: 'ready', items, error: null });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({ status: 'error', items: [], error: err?.message || String(err) });
      });
    return () => { alive = false; ctrl.abort(); };
  }, [fetcher]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onCancel) onCancel();
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return state.items;
    const q = query.trim().toLowerCase();
    return state.items.filter((c) =>
      `${c.slug || ''} ${c.name || ''} ${c.urn || ''}`.toLowerCase().includes(q),
    );
  }, [state.items, query]);

  const toggle = (urn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(urn)) next.delete(urn);
      else next.add(urn);
      return next;
    });
  };

  const submit = () => {
    if (selected.size === 0) return;
    if (onSubmit) onSubmit(Array.from(selected));
  };

  return (
    <div
      className="library-composer-popover library-composer-collections"
      role="dialog"
      aria-label="Pick collections"
      onKeyDown={onKeyDown}
    >
      <div className="library-composer-popover-head">
        <input
          ref={searchRef}
          type="text"
          className="library-composer-search"
          placeholder="Find a collection…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search collections"
        />
      </div>
      <div className="library-composer-popover-body">
        {state.status === 'loading' && (
          <p className="library-composer-empty">loading…</p>
        )}
        {state.status === 'error' && (
          <p className="library-composer-empty">couldn't load</p>
        )}
        {state.status === 'ready' && filtered.length === 0 && (
          <p className="library-composer-empty">no collections</p>
        )}
        {state.status === 'ready' && filtered.length > 0 && (
          <ul className="library-composer-collection-list">
            {filtered.map((c) => (
              <li key={c.urn}>
                <label className="library-composer-row">
                  <input
                    type="checkbox"
                    checked={selected.has(c.urn)}
                    onChange={() => toggle(c.urn)}
                  />
                  <span className="library-composer-row-label">
                    {c.name || c.slug}
                  </span>
                  <span className="library-composer-row-slug">{c.urn}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="library-composer-popover-foot">
        <button
          type="button"
          className="library-composer-submit"
          onClick={submit}
          disabled={selected.size === 0}
        >
          Add chip
        </button>
      </div>
    </div>
  );
}
